"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.ToolbarCollapsibleButtonWrapper = exports.MORE_OPTIONS_BUTTON_CLASS_NAME = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _ClickAwayListener = _interopRequireDefault(require("@material-ui/core/ClickAwayListener/ClickAwayListener"));
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _ursaIcons = require("@smartsheet/ursa-icons");
var _react = _interopRequireWildcard(require("react"));
var _reactKeyedFlattenChildren = _interopRequireDefault(require("react-keyed-flatten-children"));
var _i18n = require("../../../../i18n/i18n");
var _useTheme = require("../../../../theme/utils/useTheme");
var _flyout = require("../../../flyout");
var _visuallyhidden = require("../../../visuallyhidden");
var _FocusContext = require("../../context/FocusContext");
var _ToolbarSeparator = require("../../styled/ToolbarSeparator");
var _ToolbarToggleButton = require("../ToolbarToggleButton");
var _OverflowToolbar = require("./styled/OverflowToolbar");
var _ToolbarCollapsibleButtonWrapperStyled = require("./styled/ToolbarCollapsibleButtonWrapperStyled");
const MORE_OPTIONS_BUTTON_CLASS_NAME = "more-options-button";
exports.MORE_OPTIONS_BUTTON_CLASS_NAME = MORE_OPTIONS_BUTTON_CLASS_NAME;
const ToolbarCollapsibleButtonWrapper = _ref => {
  let {
    availableSpace,
    className,
    children,
    includeOverflowMenuSeparator,
    overflowMenuClassName,
    overflowMenuFlyoutProps,
    overflowMenuIcon,
    overflowMenuText,
    zIndex,
    setShouldRender,
    toolbarSize,
    overflowMenuMaxWidth,
    useWindowForOverflowMenuContainer
  } = _ref;
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const childrenArray = (0, _reactKeyedFlattenChildren.default)(children);
  const wrapperRef = (0, _react.useRef)(null);
  const childRefs = (0, _react.useRef)([]);
  const moreOptionsRef = (0, _react.useRef)(null);
  const allChildWidths = (0, _react.useRef)([]);
  const clickTargetRef = (0, _react.useRef)(null);
  const {
    overflowMenuPortalRef
  } = (0, _FocusContext.useFocusContext)();
  const [showMoreOptionsButton, setShowMoreOptionsButton] = (0, _react.useState)(false);
  const [overflowToolbarVisible, setOverflowToolbarVisible] = (0, _react.useState)(false);
  const hideOverflowToolbar = (0, _react.useCallback)(() => setOverflowToolbarVisible(false), []);
  const [oveflowCutoffIndex, setOverflowCutoffIndex] = (0, _react.useState)(childrenArray.length - 1);
  const {
    raf
  } = (0, _lodestarUtils.useRequestAnimationFrame)();
  childRefs.current = [];
  const clonedChildren = childrenArray.map(child => {
    if ( /*#__PURE__*/_react.default.isValidElement(child)) {
      return /*#__PURE__*/_react.default.cloneElement(child, {
        ref: ref => {
          childRefs.current = childRefs.current.filter(Boolean).concat(ref);
        }
      });
    } else {
      if (process.env.NODE_ENV !== "production") {
        console.error("Toolbar: collapsing requires every child component to forward a ref properly.");
      }
      return child;
    }
  });
  const {
    flyoutProps,
    targetProps
  } = (0, _flyout.useFlyout)({
    // This Flyout is always open because we want to render all the toolbar buttons in the DOM
    // so we can measure their widths.
    isOpen: true,
    // But, we want it to ignore the stacking order so it doesn't interact with the keyboard
    // unless it is visible.
    ignoreStackOrder: !overflowToolbarVisible,
    onCloseRequested: hideOverflowToolbar,
    placement: "bottom-end",
    targetRef: moreOptionsRef,
    offset: {
      distance: 4
    },
    portalProps: zIndex ? {
      zIndex
    } : undefined
  });
  const displayedChildren = clonedChildren.slice(0, oveflowCutoffIndex + 1);
  const overflowChildren = clonedChildren.slice(oveflowCutoffIndex + 1);
  const memoizedMaxOverflowToolbarWidth = (0, _react.useMemo)(() => {
    // We want the max width of the toolbar to be able to contain at least the largest child.
    // If the window inner width is smaller than the width of the largest child,
    // we want it to be the width of the largest child so that the child is not spilling out of the div

    // container width defaults to what we get from our props
    let containerWidth = overflowMenuMaxWidth;

    // if this override is set, we want to use the window width instead. Doing it this way to maintain backwards compatibility.
    if (useWindowForOverflowMenuContainer) {
      containerWidth = window.innerWidth - 20;
    }
    let largestChildWidth = 0;
    if (allChildWidths.current) {
      allChildWidths.current.forEach(childWidth => {
        if (childWidth > largestChildWidth) {
          largestChildWidth = childWidth;
        }
      });
    }
    if (containerWidth < largestChildWidth) {
      return largestChildWidth;
    }
    return containerWidth;
  }, [overflowMenuMaxWidth, useWindowForOverflowMenuContainer]);
  const handleInternalClick = (0, _react.useCallback)(evt => {
    clickTargetRef.current = evt.target;
  }, []);
  const handleOutsideClick = (0, _react.useCallback)(evt => {
    if (clickTargetRef.current !== evt.target) {
      hideOverflowToolbar();
    }
    clickTargetRef.current = null;
  }, [hideOverflowToolbar]);
  const shouldRenderSeparator = includeOverflowMenuSeparator && displayedChildren.length > 0;

  // Pulling this out so we have access to it in the effect but we don't necessarily need to
  // re-run the effect when it changes. This is a very sad React-ism-bandaid.
  const liveShowMoreOptionsButtonRef = (0, _lodestarUtils.useLiveRef)(showMoreOptionsButton);
  // Given the available space and the width of the more options button, calculate how many
  // children to show in the toolbar and the overflow toolbar.
  (0, _react.useEffect)(() => {
    if (availableSpace > 0 && childRefs.current) {
      // The compact UI allows for toolbar buttons to be added and removed.
      // We must do a null check in the case where we have buttons and then remove all of them resulting in an empty toolbar.
      allChildWidths.current = childRefs.current.map(child => child && child.getBoundingClientRect().width);
      const childWidth = (childRefs.current ?? []).map(childRef => childRef && childRef.getBoundingClientRect().width).reduce((previousValue, currentValue) => previousValue + currentValue, 0);
      if (childWidth > availableSpace) {
        // We need to render this first before we can calculate the true available space since
        // this button takes up a little bit of that space.
        if (!liveShowMoreOptionsButtonRef.current) {
          setShowMoreOptionsButton(true);
        }
        raf(() => {
          if (moreOptionsRef.current) {
            const newOverflowIndex = getLastIndexToDisplay({
              moreOptionsButtonWidth: moreOptionsRef.current.getBoundingClientRect().width,
              availableSpace,
              allChildWidths: allChildWidths.current || []
            });
            // Only update if necessary - this avoids a lot of unnecessary renders.
            if (newOverflowIndex !== oveflowCutoffIndex) {
              setOverflowCutoffIndex(newOverflowIndex);
            }
          }
        });
      } else {
        // Hide the more options button and show all the children.
        setShowMoreOptionsButton(false);
        setOverflowCutoffIndex(childrenArray.length - 1);
      }
      setShouldRender(true);
    }
  }, [availableSpace, liveShowMoreOptionsButtonRef, allChildWidths, moreOptionsRef, raf, oveflowCutoffIndex, childrenArray.length, setShouldRender]);
  const finalOverflowMenuIcon = overflowMenuIcon ? overflowMenuIcon : /*#__PURE__*/_react.default.createElement(_ursaIcons.MoreIcon, {
    size: 20
  });
  const overflowMenuTooltipAriaText = overflowMenuText ? overflowMenuText : i18n._( /*i18n*/"More options");
  const overflowMenuId = (0, _lodestarUtils.genId)("overflow-menu-id");
  const overflowItems = overflowChildren.length;
  const overflowMenuDescription = toolbarSize === "xxSmall" ? i18n._( /*i18n*/{
    comment: "This is to show the number of items in overflow menu",
    id: `Press enter to explore {overflowItems} more actions.`,
    values: {
      overflowItems
    }
  }) : undefined;
  return /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, /*#__PURE__*/_react.default.createElement(_ToolbarCollapsibleButtonWrapperStyled.ToolbarCollapsibleButtonWrapperStyled, {
    ref: wrapperRef,
    className: className
  }, displayedChildren, showMoreOptionsButton && shouldRenderSeparator && /*#__PURE__*/_react.default.createElement(_ToolbarSeparator.ToolbarSeparator, {
    isCollapsibleButton: true,
    toolbarSize: toolbarSize
  }), showMoreOptionsButton && /*#__PURE__*/_react.default.createElement(_ToolbarToggleButton.ToolbarToggleButton, (0, _extends2.default)({
    className: MORE_OPTIONS_BUTTON_CLASS_NAME,
    label: overflowMenuTooltipAriaText,
    onClick: () => {
      setOverflowToolbarVisible(prevShow => !prevShow);
    },
    "aria-describedby": overflowMenuId,
    tooltipText: overflowMenuTooltipAriaText,
    iconBefore: finalOverflowMenuIcon
  }, targetProps, {
    "aria-haspopup": "dialog"
    // overriding the "switch" role from ToolbarToggleButton deliberately
    // in order to easily add the "isChecked" state to this button
    ,
    role: "button",
    isChecked: overflowToolbarVisible,
    "aria-expanded": overflowToolbarVisible,
    "aria-controls": overflowToolbarVisible ? flyoutProps.id : undefined,
    isTooltipVisualOnly: true
  }))), overflowMenuDescription && /*#__PURE__*/_react.default.createElement(_visuallyhidden.VisuallyHidden, {
    id: overflowMenuId
  }, overflowMenuDescription), /*#__PURE__*/_react.default.createElement(_ClickAwayListener.default, {
    onClickAway: handleOutsideClick
  }, /*#__PURE__*/_react.default.createElement(_flyout.Flyout, (0, _extends2.default)({}, flyoutProps, {
    "aria-label": i18n._( /*i18n*/"More options"),
    ref: overflowMenuPortalRef,
    style: {
      // The overflow toolbar is always in the DOM, but we want to hide it when it
      // is not visible.
      opacity: overflowToolbarVisible ? 1 : 0,
      pointerEvents: overflowToolbarVisible ? "auto" : "none"
    },
    popperProps: {
      modifiers: [...(flyoutProps.popperProps?.modifiers ?? []), ...(overflowMenuFlyoutProps?.popperProps?.modifiers ?? [])]
    }
  }), overflowChildren.length > 0 && /*#__PURE__*/_react.default.createElement(_OverflowToolbar.OverflowToolbar, {
    maxWidth: memoizedMaxOverflowToolbarWidth,
    onClickCapture: handleInternalClick,
    themeMode: themeMode,
    className: overflowMenuClassName,
    role: "toolbar"
  }, overflowChildren))));
};
exports.ToolbarCollapsibleButtonWrapper = ToolbarCollapsibleButtonWrapper;
const getLastIndexToDisplay = args => {
  let usedWidth = 0;
  let lastIndex = 0;
  for (let i = 0; i < args.allChildWidths.length; i++) {
    usedWidth += args.allChildWidths[i];

    // if last element AND width is smaller than available space
    // -> all btns can fit
    if (i === args.allChildWidths.length - 1 && usedWidth < args.availableSpace) {
      lastIndex = i;
    }
    if (usedWidth >= args.availableSpace - args.moreOptionsButtonWidth) {
      lastIndex = i - 1;
      break;
    }
  }
  return lastIndex;
};
//# sourceMappingURL=ToolbarCollapsibleButtonWrapper.js.map